import * as m from 'mithril';
import { SearchForm } from '../../components/SearchForm';
import DateSwitcher from '../../components/DateSwitcher';
import TripResultList from './TripResultList';
import { NavBar } from '../../components/NavBar';
import LoadingIndicator from '../../components/LoadingIndicator';
import './TripSearchPage.scss';
import SearchCriteria, { LocationType } from '../../models/SearchCriteria';
import { MessageProvider } from '../../context/AppMessages';
import AppContext from '../../context/AppContext';
import { getTimeOptionGroup, getPriceOptionGroup } from './TripSearchFilterOptionsProvider'
import { FilterOption, FilterCriteria, CriteriaType, toFilterOptionSelections } from './TripSearchFilterModel';
import FilterSection from '../../components/FilterSection'
import * as dayjs from 'dayjs';
import { Dialog } from '../../components/MaterialDesignComponents';
import { TripSearchFsmContext } from '../../states/trip-search/TripSearchFsmContext';
import buildLanguageChangeActions from '../common/LanguageNavActions';
import { getIntlStringValue } from '../../helpers/StringHelpers';
import CustomSvgIcon from '../../components/CustomSvgIcon';
import { SourceLocationDTO, TripResultDTO } from '../../apiclient/ApiDtos';
import SmallCarTripResultList, { TripGroup } from './smallcar/SmallCarTripResultList';
import SmallCarTripsAds from './smallcar/SmallCarTripGroupAds';
import { ScrollToTop } from '../common/ScrollToTop';

let timeFilterOptions: FilterOption[] = getTimeOptionGroup();
let priceFilterOptions: FilterOption[] = getPriceOptionGroup();
let operatorFilterOptions: FilterOption[] = [];

interface Attrs {
    appContext: AppContext,
    smallCarSampleImageUrlPath: string,
    sourceLocations: SourceLocationDTO[],
    shuttleSources: SourceLocationDTO[],
    isOnSearchFailState: boolean,
    stateData: TripSearchFsmContext,
    isFetching: boolean,
    isFetchingCancellationRequested: boolean,
    onLocationSelectionRequested: (locationType: LocationType) => void,
    onAdvancedFilterRequested: (filterCriteria: FilterCriteria) => void,
    onBackPressed: () => void,
    onSearch: (searchCriteria: SearchCriteria) => void,
    onFilterApplied: (filterCriteria: FilterCriteria) => void,
    onTripResultSelected: (result: any) => void,
    onOperatorDetailsSelected: (tripResult: TripResultDTO) => void,
    onSearchCancellationConfirmed: () => void,
    onSearchCancellationRejected: () => void
}
function scrollToSmallCarView(id: string) {
    const el = document.getElementById(id);
    const container = document.querySelector(".app-area-main") as HTMLElement;

    if (!el || !container) return;

    container.scrollTo({
        top: el.offsetTop,
        behavior: "smooth"
    });
}
function TripSearchPage(): m.Component<Attrs> {
    const smallCarTripGroupId = "small-car-trip-group";
    let isSearchFormMinified: boolean = false;
    let primaryFilterOptions: FilterOption[] = [];

    function getUniqueOperatorOptions(tripResults: TripResultDTO[]): FilterOption<CriteriaType, string>[] {
        let operators = tripResults.map(result => result.operator);
        const distinctOperators = operators.filter(
            (operator, i, arr) => arr.findIndex(t => t.id === operator.id) === i
        );
        return distinctOperators.map(operator => {
            return {
                key: <CriteriaType>"operator",
                value: operator.id,
                icon: "",
                type: "filter",
                getLabel: (messageProvider) => {
                    return getIntlStringValue(operator.name, messageProvider.getCurrentLang());
                }
            }
        })
    }

    return {
        oninit(vnode) {
            isSearchFormMinified = vnode.attrs.stateData.isSearchFormExpanded == false;

            if (vnode.attrs.stateData.filterCriteria) {
                let filterCriteria: FilterCriteria = vnode.attrs.stateData.filterCriteria;
                let selectedPriceFilterOptions = priceFilterOptions.filter(
                    opt => opt.value == filterCriteria.getPrice());
                let selectedOperatorFilterOptions = operatorFilterOptions.filter(
                    opt => filterCriteria.getOperator().includes(opt.value));

                primaryFilterOptions = [...timeFilterOptions, ...selectedPriceFilterOptions, ...selectedOperatorFilterOptions];

            } else {
                primaryFilterOptions = [...timeFilterOptions]
            }

        },

        onremove() {
            console.debug("TripSearchPage.onremove");
        },

        view(vnode) {
            let searchCriteria = vnode.attrs.stateData.searchCriteria;
            let filterCriteria = vnode.attrs.stateData.filterCriteria;
            let searchResults = vnode.attrs.stateData.searchResults;
            let isFetching = vnode.attrs.isFetching;

            if (vnode.attrs.stateData.searchResults != null) {
                operatorFilterOptions = getUniqueOperatorOptions(vnode.attrs.stateData.searchResults)
            }

            let searchFormModel = vnode.attrs.stateData.searchFormModel;
            let messageProvider = vnode.attrs.appContext.getMessageProvider();
            let langChangeActionItems = buildLanguageChangeActions(vnode.attrs.appContext);

            let expressBusTrips: TripResultDTO[] = [];
            let smallCarTrips: TripResultDTO[] = [];

            if (searchResults != null) {
                expressBusTrips = searchResults.slice(0)
                    .sort((a, b) => filterCriteria.compare(a, b))
                    .filter(trip => !trip.isSmallCar);

                smallCarTrips = searchResults.slice(0)
                    .sort((a, b) => filterCriteria.compare(a, b))
                    .filter(trip => trip.isSmallCar);
            }

            const smallCarsTripGroup: TripGroup = smallCarTrips.reduce((group, trip) => {
                const customGroupId = trip.operator.id + trip.busType.en + trip.busType.my
                    + trip.priceRange.min.amount + trip.priceRange.min.currency + trip.priceRange.max.amount;
                const schedule = trip.departure.scheduledAt;

                if (!group[customGroupId]) {
                    group[customGroupId] = {
                        operator: trip.operator,
                        busType: trip.busType,
                        summary: trip,
                        trips: {}
                    };
                }

                if (!group[customGroupId].trips[schedule]) {
                    group[customGroupId].trips[schedule] = [];
                }

                group[customGroupId].trips[schedule].push(trip);

                return group;
            }, {} as TripGroup);

            return [
                m('div', { class: 'app-layout bg' },
                    [
                        m(NavBar, {
                            title: searchCriteria.isSmallCar ? messageProvider.get("nav_title.small_car_trips") : messageProvider.get("nav_title.express_bus_trips"),
                            canBackPress: true,
                            actionItems: langChangeActionItems,
                            onBackPressed: vnode.attrs.onBackPressed
                        }),

                        m("div.app-area-below-nav", [
                            m("div.card", [

                                m("div.card-header", { class: (searchCriteria != null && searchResults != null) ? 'd-block p-0' : 'd-none', style: "background-color:white" },
                                    m("div", { class: "d-flex py-2 px-3" }, [
                                        m("div", { class: "flex-grow-1" },
                                            (searchCriteria != null) ?
                                                m(LastSearchSummaryContent, {
                                                    searchCriteria: searchCriteria,
                                                    messageProvider: messageProvider,
                                                })
                                                : null
                                        ),

                                        (searchCriteria != null) ?
                                            m("div", { class: "d-flex " },
                                                (isSearchFormMinified) ?
                                                    m("a", {
                                                        class: "btn btn-link align-self-center p-0 text-decoration-none",
                                                        onclick: (event) => {
                                                            event.preventDefault();
                                                            isSearchFormMinified = false;
                                                        }
                                                    },
                                                        m(CustomSvgIcon, {
                                                            iconType: 'caret_arrow_down',
                                                            themeCssClass: "svg-small svg-info mr-1"
                                                        }),
                                                        m("span", { class: "text-info" }, messageProvider.get("edit"))
                                                    ) :
                                                    m("a", {
                                                        class: "btn btn-link align-self-center p-0 text-decoration-none",
                                                        onclick: (event) => {
                                                            event.preventDefault();
                                                            isSearchFormMinified = true;
                                                            searchFormModel.restore(searchCriteria);
                                                        }
                                                    },

                                                        m(CustomSvgIcon, {
                                                            iconType: 'caret_arrow_up',
                                                            themeCssClass: "svg-small svg-info mr-1"
                                                        }),
                                                        m("span", { class: "text-info" }, messageProvider.get("close"))

                                                    )
                                            ) : null
                                    ])
                                ),
                                // Header end

                                m("div.surface-level-0",
                                    [
                                        (searchCriteria != null && isSearchFormMinified && searchResults != null) ?
                                            [
                                                m(DateSwitcher, {
                                                    maximumDays: 42,
                                                    searchCriteria: searchCriteria,
                                                    searchDate: searchCriteria.isoDateString,
                                                    onDateChange: (searchCriteria: SearchCriteria) => {
                                                        searchFormModel.departureDate = searchCriteria.isoDateString;
                                                        vnode.attrs.onSearch(searchCriteria);
                                                    },
                                                }),
                                            ] : null,

                                        (isSearchFormMinified == false) ?
                                            m("div",
                                                [
                                                    m(SearchForm, {
                                                        messageProvider: messageProvider,
                                                        isSearchingSmallCar: vnode.attrs.stateData.searchCriteria.isSmallCar,
                                                        formModel: searchFormModel,
                                                        isSearchLoading: false,
                                                        onSourceLocationClick: () => {
                                                            vnode.attrs.onLocationSelectionRequested('source');
                                                        },
                                                        onDestinationLocationClick: () => {
                                                            vnode.attrs.onLocationSelectionRequested('destination');
                                                        },
                                                        onSearchClick: (searchCriteria: SearchCriteria) => {
                                                            isSearchFormMinified = true;
                                                            vnode.attrs.onSearch(searchCriteria);
                                                            primaryFilterOptions = [...timeFilterOptions];
                                                        }
                                                    })
                                                ]) : null
                                    ]
                                )
                            ]
                            ),

                            (isSearchFormMinified
                                && ((searchCriteria.isSmallCar && smallCarTrips.length > 0) || (!searchCriteria.isSmallCar && expressBusTrips.length > 0))
                                && !isFetching
                            ) ?
                                m(FilterSection, {
                                    filterOptionSelections: toFilterOptionSelections(filterCriteria, primaryFilterOptions),
                                    messageProvider: messageProvider,
                                    onAllFilterClick: () => {
                                    },
                                    onFilterOptionSelected: (option: FilterOption) => {
                                        if (option.key == "time") {
                                            let newFilterCriteria = (filterCriteria.time && filterCriteria.time == option.value) ?
                                                filterCriteria.withTime(null) :
                                                filterCriteria.withTime(option.value);
                                            vnode.attrs.onFilterApplied(newFilterCriteria);
                                        }
                                        if (option.key == "price") {
                                        }
                                        if (option.key == "operator") {
                                        }
                                        setTimeout(() => {
                                            let elems = document.getElementsByClassName('app-area-main');
                                            if (elems && elems[0]) {
                                                elems[0].scrollTop = 0;
                                            }
                                        }, 50);

                                    },
                                }) : null
                        ]),

                        m("div", { class: "bg app-area-main" }, [
                            (vnode.attrs.isFetching) ?
                                [
                                    m(LoadingIndicator),

                                    (vnode.attrs.isFetchingCancellationRequested) ?
                                        m(Dialog, {
                                            title: messageProvider.get("warning"),
                                            message: messageProvider.get("search.confirm_cancellation"),
                                            isOpen: vnode.attrs.isFetchingCancellationRequested,
                                            iconType: 'check_circle',
                                            iconThemeClass: "svg-medium svg-warning",
                                            onClose: () => {
                                                vnode.attrs.onSearchCancellationRejected();
                                            },

                                            actionItems: [
                                                {
                                                    label: "Ok",
                                                    onclick: () => {
                                                        vnode.attrs.onSearchCancellationConfirmed();
                                                    }
                                                },
                                                {
                                                    label: "Cancel",
                                                    onclick: () => {
                                                        vnode.attrs.onSearchCancellationRejected();
                                                    }
                                                }

                                            ]
                                        }) : null
                                ] : null,

                            vnode.attrs.isOnSearchFailState ?
                                m("div", { class: "pt-5 px-3" }, [
                                    m("h5", { class: "text-center font-weight-bold" },
                                        messageProvider.get("common.connection_failed_error_title")
                                    ),
                                    m("div", { class: "text-center text-muted font-italic_ mb-4" }, messageProvider.get("common.connection_failed_error_description")),
                                    m("div.text-center", m("button.btn btn-outline-primary", {
                                        onclick: () => {
                                            vnode.attrs.onSearch(searchCriteria);
                                        }
                                    },
                                        messageProvider.get("trip_search.retry"),
                                        m(CustomSvgIcon, {
                                            iconType: 'arrow_rotate_right',
                                            themeCssClass: "svg-info svg-small ml-1"
                                        }),
                                    ))
                                ])
                                :

                                (isFetching == false && searchResults != null) ?
                                    [
                                        searchCriteria.isSmallCar == true ?
                                            [
                                                m(SmallCarTripResultList, {
                                                    id: smallCarTripGroupId,
                                                    searchCriteria: searchCriteria,
                                                    tripResults: smallCarTrips.slice(0)
                                                        .filter(res => filterCriteria.matches(res)),
                                                    messageProvider: messageProvider,
                                                    onViewOperatorPhotosButtonClick: function (result: TripResultDTO) {
                                                        vnode.attrs.onOperatorDetailsSelected(result);
                                                    },

                                                    onViewSeatButtonClick: (result) => {
                                                        vnode.attrs.onTripResultSelected(result);
                                                    },
                                                }),

                                                smallCarTrips.length == 0 && expressBusTrips.length > 0 &&
                                                m("div.m-1 px-2 rounded bg-light mt-2 py-5", {
                                                    style: "background-color: rgb(209, 219, 235) !important;"
                                                },
                                                    m("div.h6 font-weight-bold mb-4 text-primary",
                                                        messageProvider.get("common.express_bus_trips")
                                                    ),
                                                    m(TripResultList, {
                                                        searchCriteria: searchCriteria,
                                                        tripResults: expressBusTrips.slice(0)
                                                            .filter(res => filterCriteria.matches(res)),
                                                        messageProvider: messageProvider,

                                                        onViewOperatorPhotosButtonClick: function (result: TripResultDTO) {
                                                            vnode.attrs.onOperatorDetailsSelected(result);
                                                        },

                                                        onViewSeatButtonClick: (result) => {
                                                            vnode.attrs.onTripResultSelected(result);
                                                        },
                                                    })
                                                )
                                            ]
                                            : [
                                                smallCarTrips.length > 0 &&
                                                m(SmallCarTripsAds,
                                                    {
                                                        searchCriteria: searchCriteria,
                                                        tripResults: smallCarTrips,
                                                        messageProvider: messageProvider,
                                                        smallCarSampleImageUrlPath: vnode.attrs.smallCarSampleImageUrlPath,
                                                        onClickToView: () => {
                                                            scrollToSmallCarView(smallCarTripGroupId);
                                                        }
                                                    }
                                                ),


                                                m(TripResultList, {
                                                    searchCriteria: searchCriteria,
                                                    tripResults: expressBusTrips.slice(0).filter(res => filterCriteria.matches(res)),
                                                    messageProvider: messageProvider,
                                                    onViewOperatorPhotosButtonClick: function (result: TripResultDTO) {
                                                        vnode.attrs.onOperatorDetailsSelected(result);
                                                    },

                                                    onViewSeatButtonClick: (result) => {
                                                        vnode.attrs.onTripResultSelected(result);
                                                    },
                                                }),

                                                smallCarTrips.length > 0 &&
                                                m("div.m-1 px-2 rounded bg-light mt-2 py-5", {
                                                    style: "background-color: rgb(209, 219, 235) !important;"
                                                },
                                                    m("div.h6 font-weight-bold mb-4 text-primary",
                                                        messageProvider.get("common.small_car_trips")
                                                    ),
                                                    m(SmallCarTripResultList, {
                                                        id: smallCarTripGroupId,
                                                        searchCriteria: searchCriteria,
                                                        tripResults: smallCarTrips,
                                                        messageProvider: messageProvider,
                                                        onViewOperatorPhotosButtonClick: function (result: TripResultDTO) {
                                                            vnode.attrs.onOperatorDetailsSelected(result);
                                                        },

                                                        onViewSeatButtonClick: (result) => {
                                                            vnode.attrs.onTripResultSelected(result);
                                                        },
                                                    }),
                                                    m(ScrollToTop)
                                                )
                                            ]
                                    ]
                                    : null
                        ]),

                    ]
                ),


            ]
        }
    }
}

var LastSearchSummaryContent: m.Component<{ searchCriteria: SearchCriteria, messageProvider: MessageProvider }> = {
    view: function (vnode) {
        var criteria = vnode.attrs.searchCriteria;
        let lang = vnode.attrs.messageProvider.getCurrentLang();
        return m("div",
            [
                m("div", { class: "_secondary-size" },
                    getIntlStringValue(criteria.sourceLocation.name, lang),
                    m(CustomSvgIcon, {
                        iconType: 'long_arrow_alt_right',
                        themeCssClass: "svg-small svg-muted mx-1"
                    }),
                    getIntlStringValue(criteria.destinationLocation.name, lang),
                ),
                m("small", [
                    m("strong.text-primary", {}, dayjs(vnode.attrs.searchCriteria.isoDateString).format("MMM D")),
                    m(CustomSvgIcon, {
                        iconType: 'circle_dot',
                        themeCssClass: "svg-small svg-muted"
                    }),
                    m("span.text-primary", vnode.attrs.messageProvider.get(criteria.travellerType.toLowerCase())),
                    m(CustomSvgIcon, {
                        iconType: 'circle_dot',
                        themeCssClass: "svg-small svg-muted"
                    }),
                    m("strong.text-primary", criteria.numberOfSeats + " " + ((criteria.numberOfSeats == 1) ? vnode.attrs.messageProvider.get("seat") : vnode.attrs.messageProvider.get("seats")))
                ])
            ]
        );
    }
}

export default TripSearchPage;